import React, { useState } from 'react';
import { CheckCircle, Clock, Trash2, AlertCircle, Play, ArrowRight, Eye, FileText, X } from 'lucide-react';
import { apiPut, apiDelete, apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { getTaskById } from '../../config/taskRegistry';
import { Button, Card, Badge, Spinner, CloseButton } from '../design-system';

const TaskList = ({ 
  tasks, 
  onTaskUpdate, 
  onTaskDelete, 
  isTherapist = false, 
  onNavigateToTask, 
  onStartTask, 
  onStatusUpdate,
  disableApiCalls = false 
}) => {
  const { userInfo } = useAuth();
  const [updatingTask, setUpdatingTask] = useState(null);
  const [deletingTask, setDeletingTask] = useState(null);
  const [startingTask, setStartingTask] = useState(null);
  const [summaryModal, setSummaryModal] = useState({ isOpen: false, task: null, patientName: '' });

  const handleStatusUpdate = async (taskId, status) => {
    if (disableApiCalls && onStatusUpdate) {
      // Use parent's function instead of making API call
      onStatusUpdate(taskId, status);
      return;
    }

    setUpdatingTask(taskId);
    
    try {
      await apiPut('/.netlify/functions/update-task-status', { taskId, status }, userInfo?.userId);
      
      const statusMessages = {
        'started': 'Task started!',
        'completed': 'Task marked as complete!',
        'pending': 'Task marked as pending'
      };
      toast.success(statusMessages[status] || 'Task status updated!');
      
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error(error.message || 'Failed to update task status');
    } finally {
      setUpdatingTask(null);
    }
  };

  const handleStart = async (taskId) => {
    if (disableApiCalls && onStartTask) {
      // Use parent's function instead of making API call
      onStartTask(taskId);
      return;
    }

    setStartingTask(taskId);
    
    try {
      const response = await apiPost('/.netlify/functions/start-task-session', {
        taskId
      }, userInfo?.userId);
      
      toast.success('Task started successfully!');
      
      // Navigate to task page immediately
      if (onNavigateToTask) {
        onNavigateToTask(taskId, response.session);
      }
    } catch (error) {
      console.error('Error starting task:', error);
      toast.error(error.message || 'Failed to start task');
    } finally {
      setStartingTask(null);
    }
  };

  const handleDelete = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }

    setDeletingTask(taskId);
    
    try {
      await apiDelete('/.netlify/functions/delete-task', { taskId }, userInfo?.userId);
      toast.success('Task deleted successfully');
      onTaskDelete();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(error.message || 'Failed to delete task');
    } finally {
      setDeletingTask(null);
    }
  };

  const handleShowSummary = (task) => {
    // Get the patient name from the task
    const patientName = task.patientName || 'Patient';
    
    setSummaryModal({
      isOpen: true,
      task: task,
      patientName: patientName
    });
  };

  const handleCloseSummary = () => {
    setSummaryModal({ isOpen: false, task: null, patientName: '' });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'started':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (tasks.length === 0) {
    return (
      <Card variant="default" padding="large" className="text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="h-8 w-8 text-gray-400" />
        </div>
        <p className="text-gray-500">No tasks assigned yet.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {tasks.map(task => {
        // Get task metadata from registry
        const taskConfig = getTaskById(task.taskTypeId);
        
        return (
          <Card key={task.taskId} variant="default" padding="medium" className={`hover:shadow-sm transition-shadow ${
            task.status === 'completed' ? 'opacity-60' : ''
          }`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-2 flex-1 min-w-0">
                {task.taskTypeId ? (
                  <Play className="h-4 w-4 text-indigo-600 mt-0.5" />
                ) : (
                  <FileText className="h-4 w-4 text-amber-600 mt-0.5" />
                )}
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h4 className={`font-medium text-sm ${task.status === 'completed' ? 'text-gray-500' : 'text-gray-900'}`}>
                      {taskConfig?.name || task.title || 'Untitled Task'}
                    </h4>
                  </div>
                  
                  <p className="text-xs text-gray-600 mb-1">
                    {taskConfig?.description || task.description ? 
                      ((taskConfig?.description || task.description).length > 80 ? 
                        (taskConfig?.description || task.description).substring(0, 80) + '...' : 
                        (taskConfig?.description || task.description)
                      ) : 
                      'No description'
                    }
                  </p>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs text-gray-500">
                    <span>Created: {new Date(task.createdAt).toLocaleDateString()}</span>
                    {task.dueDate && (
                      <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                    )}
                    {task.startedAt && (
                      <span className="text-blue-600">
                        Started: {new Date(task.startedAt).toLocaleDateString()}
                      </span>
                    )}
                    {task.completedAt && (
                      <span className="text-green-600">
                        Completed: {new Date(task.completedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  
                  {task.completionNotes && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-xs text-green-800">
                        <strong>Completion Notes:</strong> {task.completionNotes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col items-end space-y-1">
                {/* Only show status badge for completed and overdue tasks */}
                {(task.status === 'completed' || (task.status === 'pending' && isOverdue(task.dueDate))) && (
                  <Badge variant={task.status === 'completed' ? 'success' : 'error'} size="sm">
                    {task.status === 'completed' ? 'Completed' : 'Overdue'}
                  </Badge>
                )}
                
                <div className="flex flex-col space-y-1">
                {/* AI Task - Start button */}
                {task.taskTypeId && task.status === 'pending' && !isTherapist && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleStart(task.taskId)}
                    disabled={startingTask === task.taskId}
                    className="h-[44px] w-20"
                  >
                    {startingTask === task.taskId ? <Spinner size="small" /> : 'Start'}
                  </Button>
                )}

                {/* AI Task - Continue button */}
                {task.taskTypeId && task.status === 'started' && !isTherapist && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleStart(task.taskId)}
                    disabled={startingTask === task.taskId}
                    className="h-[44px] w-20"
                  >
                    {startingTask === task.taskId ? <Spinner size="small" /> : 'Continue'}
                  </Button>
                )}

                {/* Dynamic View Summary button (for completed tasks with ResultsSummary components) */}
                {task.taskTypeId && task.status === 'completed' && task.completionData && isTherapist && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleShowSummary(task)}
                    className="flex items-center"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View Summary
                  </Button>
                )}
                
                {/* Regular task buttons */}
                {!task.taskTypeId && task.status === 'pending' && !isTherapist && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleStatusUpdate(task.taskId, 'started')}
                    disabled={updatingTask === task.taskId}
                    className="h-[44px] w-16"
                  >
                    {updatingTask === task.taskId ? <Spinner size="small" /> : (
                      <>
                        <Play className="h-3 w-3 mr-1" />
                        Start
                      </>
                    )}
                  </Button>
                )}
                
                {!task.taskTypeId && task.status === 'started' && !isTherapist && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handleStatusUpdate(task.taskId, 'completed')}
                    disabled={updatingTask === task.taskId}
                    className="h-[44px] w-16"
                  >
                    {updatingTask === task.taskId ? <Spinner size="small" /> : (
                      <>
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Complete
                      </>
                    )}
                  </Button>
                )}
                
                {!task.taskTypeId && task.status === 'completed' && !isTherapist && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleStatusUpdate(task.taskId, 'pending')}
                    disabled={updatingTask === task.taskId}
                    className="h-[44px] w-16"
                  >
                    {updatingTask === task.taskId ? <Spinner size="small" /> : (
                      <>
                        <Clock className="h-3 w-3 mr-1" />
                        Reset
                      </>
                    )}
                  </Button>
                )}
                
                {isTherapist && (
                  <Button
                    variant="error"
                    size="sm"
                    onClick={() => handleDelete(task.taskId)}
                    disabled={deletingTask === task.taskId}
                  >
                    {deletingTask === task.taskId ? <Spinner size="small" /> : (
                      <>
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </>
                    )}
                  </Button>
                )}
                </div>
              </div>
            </div>
          </Card>
        );
      })}
      {/* Dynamic Summary Modal */}
      {summaryModal.isOpen && summaryModal.task && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card variant="default" padding="none" className="max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Task Summary - {summaryModal.patientName}
              </h3>
              <CloseButton onClick={handleCloseSummary} />
            </div>
            <div className="p-6">
              {(() => {
                const taskConfig = getTaskById(summaryModal.task.taskTypeId);
                const TaskComponent = taskConfig?.component;
                
                
                if (TaskComponent?.ResultsSummary) {
                  return React.createElement(TaskComponent.ResultsSummary, {
                    task: summaryModal.task,
                    completionData: summaryModal.task.completionData,
                    patientName: summaryModal.patientName
                  });
                } else {
                  return (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No summary available for this task.</p>
                    </div>
                  );
                }
              })()}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TaskList; 